import { App, Button, Card, Checkbox, Flex, Form, type FormProps, Input, Typography } from 'antd';
import { useMutation } from 'jsonapi-react';
import PropTypes from 'prop-types';
import { type ReactNode, useEffect, useMemo } from 'react';

import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import { type Agency } from '../../../../entities';

const SUCCESS_UPDATE_MESSAGE = 'Agency updated successfully!';
const SUCCESS_ADD_MESSAGE = 'Agency added successfully!';

interface DataType {
  agency: Agency;
  onSuccess: (agency: Agency) => void;
  onCancel: () => void;
}

const AgencyForm = (props: DataType): ReactNode => {
  const { notification } = App.useApp();
  const { agency, onSuccess, onCancel } = props;
  const [formInstance] = Form.useForm();
  const isEditMode = useMemo(() => !!agency?.id, [agency?.id]);
  const [mutateAgency, agencies] = useMutation<Agency>(
    isEditMode ? ['agency', agency.id] : ['agency']
  );
  const initialValues = useMemo(
    () => ({
      ...agency,
    }),
    [agency]
  );

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const onSubmit: FormProps<Agency>['onFinish'] = (submitted) => {
    from(mutateAgency(submitted))
      .pipe(
        switchMap((res) =>
          res.error ? throwError(() => new Error(res.error?.title)) : of(res.data)
        ),
        tap((ct) => {
          notification.success({
            description: isEditMode ? SUCCESS_UPDATE_MESSAGE : SUCCESS_ADD_MESSAGE,
            message: '',
          });
          onSuccess(ct!);
        }),
        catchError((error: Error) => {
          const [description, title, ..._err] = error?.message.split('\n');
          notification.error({
            description: description ?? 'No meals are available',
            message: title ?? '',
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={formInstance}
          id="cycle-menu-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item>
            <Typography.Title level={4}>
              {isEditMode ? 'Update Agency' : 'New Agency'}
            </Typography.Title>
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="name" label="Name">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input />
          </Form.Item>
          <Form.Item
            colon={false}
            name="isActive"
            valuePropName="checked"
            style={{ marginBlockEnd: 0 }}
          >
            <Checkbox>Is Active</Checkbox>
          </Form.Item>

          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse' }}>
              <Button
                block
                type="primary"
                disabled={agencies.isLoading}
                loading={agencies.isLoading}
                htmlType="submit"
              >
                Save
              </Button>
              <Button block onClick={onCancel} disabled={agencies.isLoading}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

AgencyForm.propTypes = {
  defaultPageSize: PropTypes.number,
};

AgencyForm.defaultProps = {
  defaultPageSize: 10,
  onVisibleColumnsChange: () => {},
  onSelectRowsChange: () => {},
};

AgencyForm.displayName = 'EditAgencySection';
export default AgencyForm;
